import {
    adjustMoneyMapForHighBills,
    buildStaticRuleSetDisplays,
    computeRuleSetDisplays,
    NO_DATE_HEADLINE,
} from '@fruitful/conversational-mm';
import {
    OnboardingMoneyMapPreview,
    type OnboardingMoneyMapPreviewMode,
} from '@fruitful/money-map';
import { buildAhaResult, buildSimulation } from '@fruitful/money-map-simulator';
import {
    type AhaMomentIntake,
    type DebtAhaResult,
    type OnboardingChatResults,
    type RuleSetDisplay,
    type SavingsAhaResult,
} from '@fruitful/types';
import { useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { FontFamilyName, isWeb, Text, View, XStack, YStack } from 'smoothie';
import { LinearGradient } from 'tamagui/linear-gradient';

interface MoneyMapTotals {
    incomeTotalCents: number;
    billsTotalCents: number;
    spendTotalCents: number;
    goalsTotalCents: number;
}

export const useVersion2Data = (results: {
    moneyMap: MoneyMapTotals;
    rawChatResults: AhaMomentIntake | null;
}) => {
    const { moneyMap, rawChatResults } = results;

    const adjusted = useMemo(
        () => adjustMoneyMapForHighBills(moneyMap),
        [moneyMap],
    );

    const wasAdjusted = useMemo(
        () =>
            adjusted.billsTotalCents !== moneyMap.billsTotalCents ||
            adjusted.goalsTotalCents !== moneyMap.goalsTotalCents,
        [adjusted, moneyMap],
    );

    const { ruleSetDisplays, hideDates } = useMemo(() => {
        if (rawChatResults == null) {
            return { ruleSetDisplays: null, hideDates: true };
        }

        const { spendTotalCents, goalsTotalCents } = adjusted;
        const noGoalsMoney = goalsTotalCents <= 0;

        const simulation = buildSimulation({
            ...rawChatResults,
            monthlySpendCents: spendTotalCents,
            monthlyGoalsSpendCents: goalsTotalCents,
        });

        const displayResult =
            simulation != null && goalsTotalCents > 0
                ? computeRuleSetDisplays(simulation)
                : {
                      ruleSets: buildStaticRuleSetDisplays(rawChatResults),
                      exceedsDateThreshold: true,
                      emergencyFundUnreachable: true,
                      noGoalsMoney: true,
                  };

        return {
            ruleSetDisplays:
                displayResult.ruleSets.length > 0
                    ? displayResult.ruleSets
                    : null,
            hideDates: noGoalsMoney || displayResult.exceedsDateThreshold,
        };
    }, [rawChatResults, adjusted]);

    return { adjusted, wasAdjusted, ruleSetDisplays, hideDates };
};

export interface MoneyMapProjectionDisplay {
    headline: string;
    dateSuffix: string;
    mode: OnboardingMoneyMapPreviewMode;
    showSystemPreview: boolean;
    adjusted: MoneyMapTotals;
    ruleSetDisplays: RuleSetDisplay[] | null;
}

/**
 * Derives the version-2 "money map projection" display data (debt-free headline
 * + timeline/system graph inputs) from a CMM thread result. Returns null when the
 * result is missing or is not a version-2 artifact.
 *
 * Hooks are called unconditionally so this is safe to invoke before the caller
 * knows whether `results` is populated.
 */
export const useMoneyMapProjectionDisplay = (
    results: OnboardingChatResults | null,
): MoneyMapProjectionDisplay | null => {
    const v2Data = useVersion2Data({
        moneyMap: results?.moneyMap ?? {
            incomeTotalCents: 0,
            billsTotalCents: 0,
            spendTotalCents: 0,
            goalsTotalCents: 0,
        },
        rawChatResults: results?.rawChatResults ?? null,
    });

    if (results == null || results.artifactData?.version !== 2) {
        return null;
    }

    const ahaResult = buildAhaResult(results);

    const adjustedAhaResult = v2Data.wasAdjusted
        ? (buildAhaResult({
              ...results,
              moneyMap: v2Data.adjusted,
          }) as DebtAhaResult | SavingsAhaResult)
        : (ahaResult as DebtAhaResult | SavingsAhaResult);

    const showSystemPreview =
        adjustedAhaResult.type !== 'debt' ||
        v2Data.wasAdjusted ||
        v2Data.hideDates;

    const headline = showSystemPreview
        ? NO_DATE_HEADLINE
        : adjustedAhaResult.displayHeadline;

    const dateSuffix =
        !showSystemPreview && adjustedAhaResult.debtFreeDate !== ''
            ? adjustedAhaResult.debtFreeDate
            : '';

    const mode: OnboardingMoneyMapPreviewMode = showSystemPreview
        ? 'system'
        : 'timeline';

    return {
        headline,
        dateSuffix,
        mode,
        showSystemPreview,
        adjusted: v2Data.adjusted,
        ruleSetDisplays: v2Data.ruleSetDisplays,
    };
};

/**
 * The headline + timeline/system projection graph, extracted from HomeGoalTile so
 * it can be reused as a standalone card (e.g. the no-session-booked home widget).
 */
export const MoneyMapProjectionGraphic = ({
    display,
    graphOnly = false,
}: {
    display: MoneyMapProjectionDisplay;
    /**
     * When true, renders only the projection graph (no spend/goals slider or
     * goals list) in a landscape aspect ratio matching the Figma graph card.
     */
    graphOnly?: boolean;
}) => {
    const { headline, dateSuffix, mode, adjusted, ruleSetDisplays } = display;

    const isGraphOnlyTimeline = graphOnly && mode === 'timeline';
    const isGraphOnlySystem = graphOnly && mode === 'system';

    // Clip the system diagram just past the top of the goals section so the
    // card shows income + expenses + a peek of the first goal (then fades out),
    // matching the Figma. Anchoring to the reported goals-box Y keeps this
    // correct regardless of how many goals the member has.
    const [goalsSectionY, setGoalsSectionY] = useState(0);
    const GOAL_PEEK = 72;
    // White padding inside the box, around the diagram (matching the Figma,
    // where the system sits inset within the white card).
    const SYSTEM_PAD_X = 16;
    const SYSTEM_PAD_TOP = 12;
    const systemClipHeight =
        isGraphOnlySystem && goalsSectionY > 0
            ? Math.round(SYSTEM_PAD_TOP + goalsSectionY + GOAL_PEEK)
            : undefined;
    // The fade starts a little below the top of the goals section so the first
    // goal's label stays legible while the amount/date beneath it tapers out.
    const systemFadeHeight = 64;

    const preview = (
        <OnboardingMoneyMapPreview
            mode={mode}
            disableAnimations
            graphOnly={graphOnly}
            incomeTotalCents={adjusted.incomeTotalCents}
            billsTotalCents={adjusted.billsTotalCents}
            spendTotalCents={adjusted.spendTotalCents}
            goalsTotalCents={adjusted.goalsTotalCents}
            ruleSetDisplays={ruleSetDisplays}
            onSystemBoxPositions={
                isGraphOnlySystem
                    ? (positions) => {
                          const y = positions.goals.y;
                          if (y > 0 && Math.abs(y - goalsSectionY) > 1) {
                              setGoalsSectionY(y);
                          }
                      }
                    : undefined
            }
        />
    );

    return (
        <YStack gap={graphOnly ? 16 : 24} alignItems="center">
            <XStack flexWrap="wrap" gap={4} justifyContent="center">
                <Text
                    fontSize={graphOnly ? 24 : 40}
                    lineHeight="100%"
                    letterSpacing={graphOnly ? -1.2 : -2}
                    textAlign="center"
                    style={{
                        fontFamily: FontFamilyName.SourceSerifRegular,
                    }}
                >
                    {headline} {dateSuffix !== '' && dateSuffix}
                </Text>
            </XStack>
            <View
                style={
                    isGraphOnlyTimeline
                        ? { width: '100%', aspectRatio: 322 / 240 }
                        : isGraphOnlySystem
                          ? {
                                width: '100%',
                                maxWidth: 354,
                                height: systemClipHeight,
                            }
                          : { width: '100%', maxWidth: 354, height: 374 }
                }
                overflow="hidden"
                alignSelf="center"
                position="relative"
                borderRadius={isGraphOnlySystem ? 16 : undefined}
                backgroundColor={isGraphOnlySystem ? '$white' : undefined}
            >
                {isGraphOnlySystem ? (
                    <View
                        paddingHorizontal={SYSTEM_PAD_X}
                        paddingTop={SYSTEM_PAD_TOP}
                    >
                        {preview}
                    </View>
                ) : (
                    preview
                )}
                {isGraphOnlySystem && (
                    // Fade + blur the bottom of the system diagram (the lower
                    // goals) out to white, matching the Figma "money system"
                    // card. The mask ramps the blur in gradually (no hard line
                    // at the top) and the gradient ramps the white all the way
                    // to opaque so the goal fully dissolves before the clipped
                    // bottom edge (no hard line at the bottom).
                    <View
                        pointerEvents="none"
                        position="absolute"
                        left={0}
                        right={0}
                        bottom={0}
                        height={systemFadeHeight}
                        overflow="hidden"
                        borderBottomLeftRadius={16}
                        borderBottomRightRadius={16}
                        style={
                            isWeb
                                ? {
                                      backdropFilter: 'blur(18px)',
                                      WebkitBackdropFilter: 'blur(18px)',
                                      // Ramp the blur in from the top AND
                                      // feather its left/right edges so there's
                                      // no hard line on any side (the two masks
                                      // are intersected).
                                      maskImage:
                                          'linear-gradient(to bottom, transparent 0%, black 70%), linear-gradient(to right, transparent 0, black 8px, black calc(100% - 8px), transparent 100%)',
                                      WebkitMaskImage:
                                          'linear-gradient(to bottom, transparent 0%, black 70%), linear-gradient(to right, transparent 0, black 8px, black calc(100% - 8px), transparent 100%)',
                                      maskComposite: 'intersect',
                                      WebkitMaskComposite: 'source-in',
                                  }
                                : undefined
                        }
                    >
                        <LinearGradient
                            colors={[
                                'rgba(255,255,255,0)',
                                'rgba(255,255,255,1)',
                            ]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 0, y: 1 }}
                            style={StyleSheet.absoluteFill}
                        />
                    </View>
                )}
            </View>
        </YStack>
    );
};
